/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { X } from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import components */
import SystemAnomalyIcons from '@/components/tabs/overview/system/SystemAnomalyIcons';
import CodeBlock from '@/components/code/CodeBlock';

/* Import libraries */
import type {
    SystemInformation,
    SystemInformationSensor,
    SystemInformationSensorKey,
} from '@/lib/sensors/presentation/systemInformation';
import type { OverviewSystemStatusCalculation } from '@/lib/overview/overviewSystemAggregation';
import {
    resolveTimeSeriesTrafficLightSeverityDefault,
    resolveTimeSeriesTrafficLightStatusLabel,
} from '@/lib/timeSeries/timeSeriesValue';

interface SystemInformationModalProps {
    information: SystemInformation;
    isOpen: boolean;
    onClose: () => void;
    statusCalculations?: readonly OverviewSystemStatusCalculation[];
}

const SENSOR_LABELS: Record<SystemInformationSensorKey, string> = {
    gas: 'Gas',
    gridFeed: 'Grid Feed',
    gridSupply: 'Grid Supply',
    heat: 'Heat',
    photovoltaics: 'Photovoltaics',
    water: 'Water',
};

interface SystemInformationRow {
    isTechnicalValue?: boolean;
    label: string;
    sensors?: SystemInformationSensor[];
    value: string | null;
}

interface SystemInformationSourceValue {
    anomaly: string;
    sensor: string;
    urn: string;
    value: string;
}

function getSystemInformationSourceValues(
    statusCalculations: readonly OverviewSystemStatusCalculation[],
    unavailableValueLabel: string,
): SystemInformationSourceValue[] {
    return statusCalculations.flatMap((calculation) => (
        calculation.anomalyConfig.statusDefinition.sourceSensorTypes.map((sensorType) => {
            const urnEntry = calculation.item.urn.entries().find((entry) => (
                entry.key === sensorType || entry.aliases?.includes(sensorType)
            ));
            const formattedValue = calculation.item.formattedValue.get(sensorType);
            const unit = calculation.item.unit.get(sensorType);

            return {
                anomaly: calculation.anomalyConfig.label,
                sensor: calculation.sensor,
                urn: urnEntry?.value ?? sensorType,
                value: formattedValue === null
                    ? unavailableValueLabel
                    : `${formattedValue}${unit ? ` ${unit}` : ''}`,
            };
        })
    ));
}

function SystemInformationModal({
    information,
    isOpen,
    onClose,
    statusCalculations = [],
}: SystemInformationModalProps) {
    const { t } = useI18n();
    const [activeTab, setActiveTab] = useState<'code' | 'information' | 'sources' | 'status'>('information');
    const systemName = information.description ?? information.system;
    const hasStatusCalculations = statusCalculations.length > 0;
    const hasCombinedWaterStatus = statusCalculations.some(({ anomalyConfig }) => (
        anomalyConfig.group === 'WL'
        && anomalyConfig.statusDefinition.sourceSensorTypes.includes('dtc')
        && anomalyConfig.statusDefinition.sourceSensorTypes.includes('dtc_off')
    ));
    const sourceValues = getSystemInformationSourceValues(statusCalculations, t('Not available'));
    const calculationCodes = Array.from(new Map(
        statusCalculations
            .filter((calculation) => calculation.anomalyConfig.statusDefinition.calcCode)
            .map((calculation) => [
                calculation.anomalyConfig.key,
                {
                    code: calculation.anomalyConfig.statusDefinition.calcCode!,
                    label: calculation.anomalyConfig.label,
                },
            ]),
    ).values());
    const codeBlockTheme = document.documentElement.getAttribute('data-theme') === 'dark'
        ? 'dark'
        : 'light';
    const rows: SystemInformationRow[] = [
        { isTechnicalValue: true, label: t('System'), value: information.system },
        { label: t('Description'), value: information.description },
        { label: t('Address'), value: information.address },
        ...Object.entries(SENSOR_LABELS).map(([key, label]) => ({
            isTechnicalValue: true,
            label: t(label),
            sensors: information.sensors[key as SystemInformationSensorKey],
            value: null,
        })),
    ];
    const hasDisabledSensors = Object.values(information.sensors)
        .flat()
        .some((sensor) => !sensor.isEnabled);

    if (!isOpen) {
        return null;
    }

    return (
        <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="system-information-modal-title">
            <div className="modal-box flex max-h-[calc(100vh-2rem)] max-w-4xl flex-col overflow-visible">
                <div className="mb-5 flex items-center justify-between gap-4">
                    <div>
                        <SystemAnomalyIcons anomalySensorCounts={information.anomalySensorCounts} />
                        <h2 id="system-information-modal-title" className="mb-0 mt-2 text-xl font-semibold">
                            {t('System information')} &quot;{systemName}&quot;
                        </h2>
                    </div>
                    <button
                        className="btn btn-ghost btn-sm btn-circle"
                        type="button"
                        onClick={onClose}
                        aria-label={t('Close')}
                        title={t('Close')}
                    >
                        <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>

                <div className={`flex min-h-0 flex-1 flex-col${hasStatusCalculations ? ' layouts-code-tabs' : ''}`}>
                    {hasCombinedWaterStatus ? (
                        <p className="mb-4 text-sm text-base-content/80">
                            {t('Water status uses the higher of the latest loss and data-quality DTC values.')}{' '}
                            <code>max(dtc, dtc_off)</code>
                        </p>
                    ) : null}
                    {hasStatusCalculations ? (
                        <div className="tabs tabs-lift" role="tablist" aria-label={t('System information')}>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'information'}
                                className={`tab${activeTab === 'information' ? ' tab-active' : ''}`}
                                onClick={() => setActiveTab('information')}
                            >
                                {t('System information')}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'sources'}
                                className={`tab${activeTab === 'sources' ? ' tab-active' : ''}`}
                                onClick={() => setActiveTab('sources')}
                            >
                                {t('Source values')}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'status'}
                                className={`tab${activeTab === 'status' ? ' tab-active' : ''}`}
                                onClick={() => setActiveTab('status')}
                            >
                                {t('Status calculation')}
                            </button>
                            {calculationCodes.length > 0 ? (
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'code'}
                                    className={`tab${activeTab === 'code' ? ' tab-active' : ''}`}
                                    onClick={() => setActiveTab('code')}
                                >
                                    {t('Code')}
                                </button>
                            ) : null}
                        </div>
                    ) : null}

                    <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                    {activeTab === 'information' ? (
                        <div className={hasStatusCalculations ? 'layouts-code-surface overflow-x-auto' : 'w-full overflow-x-auto rounded-box border border-base-content/20'}>
                            <table className="table table-sm w-full">
                                <thead className="bg-base-content/10 text-sm text-base-content/80">
                                    <tr>
                                        <th className="w-56 px-4 py-3">{t('Property')}</th>
                                        <th className="px-4 py-3">{t('Value')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-base-300">
                                    {rows.map((row) => (
                                        <tr key={row.label} className="even:bg-base-content/5 hover:bg-base-content/10">
                                            <td className="align-top px-4 py-3 font-medium">{row.label}</td>
                                            <td className={`align-top break-all px-4 py-3 text-base-content/80 ${
                                                row.isTechnicalValue ? 'font-mono text-xs md:text-sm' : ''
                                            }`}>
                                                {row.sensors
                                                    ? row.sensors.length > 0
                                                        ? renderSensors(row.sensors)
                                                        : t('Not available')
                                                    : row.value ?? t('Not available')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {hasDisabledSensors ? (
                                <p className="mb-0 px-4 pb-3 pt-1 text-sm text-base-content/70">
                                    <sup>*)</sup>: {t('Disabled sensor')}
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    {activeTab === 'sources' ? (
                        <div className="layouts-code-surface overflow-x-auto">
                            <table className="table table-zebra font-sans">
                                <thead>
                                    <tr>
                                        <th>{t('Anomaly')}</th>
                                        <th>{t('Sensor')}</th>
                                        <th>{t('Current value')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sourceValues.map((source) => (
                                        <tr key={`${source.anomaly}-${source.sensor}-${source.urn}`}>
                                            <td>{t(source.anomaly)}</td>
                                            <td><code>{source.urn}</code></td>
                                            <td>{source.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}

                    {activeTab === 'status' ? (
                        <div className="layouts-code-surface time-series-traffic-light-legend overflow-x-auto">
                            <table className="table table-zebra font-sans">
                                <thead>
                                    <tr>
                                        <th>{t('Anomaly')}</th>
                                        <th>{t('Sensor')}</th>
                                        <th>{t('State')}</th>
                                        <th>{t('Rule')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {statusCalculations.map((calculation) => {
                                        const { statusDefinition } = calculation.anomalyConfig;
                                        const severity = resolveTimeSeriesTrafficLightSeverityDefault(
                                            calculation.status,
                                            statusDefinition.trafficLight,
                                        );
                                        const rule = statusDefinition.trafficLight.rules.find((item) => item.severity === severity);

                                        return (
                                            <tr key={`${calculation.anomalyConfig.key}-${calculation.sensor}`}>
                                                <td>{t(calculation.anomalyConfig.label)}</td>
                                                <td><code>{calculation.sensor}</code></td>
                                                <td>
                                                    <div className="inline-flex items-center gap-3">
                                                        <span className="time-series-kpi__legend-item time-series-kpi__legend-item--active">
                                                            <span
                                                                className={`time-series-kpi__legend-swatch time-series-kpi__legend-swatch--${severity}`}
                                                                aria-hidden="true"
                                                            />
                                                        </span>
                                                        <span>{t(resolveTimeSeriesTrafficLightStatusLabel(
                                                            calculation.status,
                                                            statusDefinition.trafficLight,
                                                        ))}</span>
                                                    </div>
                                                </td>
                                                <td>{rule?.title ?? t('Otherwise')}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : null}

                    {activeTab === 'code' ? (
                        <div className="layouts-code-surface">
                            {calculationCodes.map(({ code, label }) => (
                                <details
                                    key={label}
                                    className="mb-3 last:mb-0"
                                    open={calculationCodes.length === 1}
                                >
                                    <summary className="cursor-pointer font-semibold">
                                        {t(label)}
                                    </summary>
                                    <div className="mt-3">
                                        <CodeBlock
                                            code={code}
                                            language="typescript"
                                            theme={codeBlockTheme}
                                            wrapperClassName="mb-0"
                                        />
                                    </div>
                                </details>
                            ))}
                        </div>
                    ) : null}
                    </div>
                </div>

                <div className="modal-action">
                    <button className="btn btn-primary" type="button" onClick={onClose}>
                        {t('Close')}
                    </button>
                </div>
            </div>
            <button className="modal-backdrop" type="button" onClick={onClose} aria-label={t('Close')}>
                {t('Close')}
            </button>
        </div>
    );
}

function renderSensors(sensors: SystemInformationSensor[]): ReactNode {
    if (sensors.length === 0) {
        return null;
    }

    return sensors.map((sensor, index) => (
        <Fragment key={sensor.urn}>
            {index > 0 ? ', ' : null}
            {sensor.urn}
            {!sensor.isEnabled ? <sup>*)</sup> : null}
        </Fragment>
    ));
}

export default SystemInformationModal;
